import { CenteredLayout, ContentHeader, Css, useComputed } from "@homebound/beam";
import { useMemo } from "react";
import { LoadingTable } from "src/components/LoadingTable";
import { useRouteParams } from "src/hooks/useRouteParams";
import { celisImpactTotals } from "src/routes/projects/change-events/guided/utils";
import { WorkflowFormState } from "src/routes/projects/change-events/guided/workflows/types";
import { GuidedChangeEventParams } from "src/routes/routesDef";
import { addEntityParam } from "src/RouteUrls";
import { fail, queriesResult, queryResult } from "src/utils";
import {
  ReviewProjectImpact_CheckoutConfigFragment,
  ReviewProjectImpact_ProjectCostCodeFragment,
  ReviewProjectImpact_StructureFragment,
  ReviewProjectScheduleImpactQueryVariables,
  useReviewImpactProjectQuery,
  useReviewProjectImpactChangeEventQuery,
  useReviewProjectImpactCheckoutConfigQuery,
  useReviewProjectImpactComputeCostQuery,
  useReviewProjectScheduleImpactQuery,
} from "./3.ReviewImpactStep.generated";
import { ApplyConfigurationFormInput } from "./applyConfigurationForm";
import { ProjectBudgetTable } from "./components/ProjectBudgetTable";
import { ProjectImpactCards } from "./components/ProjectImpactCards";
import { ScheduleImpactTable } from "./components/ScheduleImpactTable";
import {
  createProjectBudgetRows,
  resolveCheckoutConfigFields,
  reviewImpactBudgetTotals,
  ReviewImpactNewBudgetRow,
} from "./reviewImpactUtils";

type ReviewImpactStepProps = { formState: WorkflowFormState<ApplyConfigurationFormInput> };

/**
 * A read-only look at what the configuration picked on step 1 does to the project's budget, scope and schedule.
 *
 * The configuration may not have been saved as a change event yet: with no signed contract the workflow applies it
 * directly later, so there are no `ChangeEventLineItem`s to read and the impact has to be computed from the ready
 * plan instead. Each branch is its own component so neither has to run the other's queries.
 */
export function ReviewImpactStep(props: ReviewImpactStepProps) {
  const { formState } = props;
  const { projectId, idOrAdd } = useRouteParams<GuidedChangeEventParams>();
  const hasChangeEvent = idOrAdd !== addEntityParam;
  const checkoutConfigId = useComputed(() => formState.checkoutConfigId.value, [formState]);

  const projectQuery = useReviewImpactProjectQuery({ variables: { projectId } });
  const changeEventQuery = useReviewProjectImpactChangeEventQuery({
    variables: { changeEventId: idOrAdd },
    skip: !hasChangeEvent,
  });
  const nonChangeEventQuery = useReviewProjectImpactCheckoutConfigQuery({
    variables: { structureId: projectId, configId: checkoutConfigId ?? fail("No checkout config id") },
    skip: hasChangeEvent,
  });
  // The query takes exactly one of the two, and resolves the main house/ADU side of the config off the project itself
  const scheduleImpactVariables: ReviewProjectScheduleImpactQueryVariables = hasChangeEvent
    ? { projectId, changeEventId: idOrAdd }
    : { projectId, checkoutConfigId };

  return queriesResult([projectQuery, changeEventQuery, nonChangeEventQuery] as const, {
    data: (projectData, maybeChangeEventData, maybeNonChangeEventData) => {
      const { project, projectItemsPage } = projectData;
      const projectItemsCount = projectItemsPage.pageInfo.totalCount;
      if (maybeNonChangeEventData) {
        return (
          <ComputedConfigImpactCost
            structure={maybeNonChangeEventData.structure}
            checkoutConfig={maybeNonChangeEventData.checkoutConfig}
            projectItemsCount={projectItemsCount}
            projectCostCodes={project.projectCostCodes}
            scheduleImpactVariables={scheduleImpactVariables}
            {...resolveCheckoutConfigFields(
              maybeNonChangeEventData.checkoutConfig,
              maybeNonChangeEventData.structure.isAdu,
            )}
          />
        );
      } else {
        return (
          <ReviewImpactView
            currentItemCount={projectItemsCount}
            newItemCount={projectItemsCount + celisImpactTotals(maybeChangeEventData.changeEvent.lineItems).netItems}
            projectCostCodes={project.projectCostCodes}
            newBudgetRows={maybeChangeEventData.changeEvent.lineItems.map((cei) => ({
              costCode: cei.item.costCode,
              totalCostInCents: cei.totalCostInCents,
              totalPriceInCents: cei.totalPriceInCents,
            }))}
            isReplacingConfig={false}
            scheduleImpactVariables={scheduleImpactVariables}
          />
        );
      }
    },
  });
}

type ComputedConfigImpactCostProps = {
  projectItemsCount: number;
  projectCostCodes: ReviewProjectImpact_ProjectCostCodeFragment[];
  structure: ReviewProjectImpact_StructureFragment;
  checkoutConfig: ReviewProjectImpact_CheckoutConfigFragment;
  readyPlanId: string;
  readyPlanOptionIds: string[];
  versionId: string | undefined;
  scheduleImpactVariables: ReviewProjectScheduleImpactQueryVariables;
};

function ComputedConfigImpactCost(props: ComputedConfigImpactCostProps) {
  const { projectItemsCount, projectCostCodes, readyPlanId, readyPlanOptionIds, versionId, scheduleImpactVariables } =
    props;
  const query = useReviewProjectImpactComputeCostQuery({ variables: { readyPlanId, readyPlanOptionIds, versionId } });
  return queryResult(query, ({ computeReadyPlanCost }) => (
    <ReviewImpactView
      projectCostCodes={projectCostCodes}
      currentItemCount={projectItemsCount}
      // The initial workflow replaces the whole configuration, so the computed items *are* the new scope
      newItemCount={computeReadyPlanCost.protoProjectItems.length}
      newBudgetRows={computeReadyPlanCost.protoProjectItems.map((ppi) => ({
        costCode: ppi.item.costCode,
        totalCostInCents: ppi.totalCostInCents,
        // `computeReadyPlanCost` only prices the trade cost, so there is nothing to show as a price/markup
        totalPriceInCents: undefined,
      }))}
      isReplacingConfig={true}
      scheduleImpactVariables={scheduleImpactVariables}
    />
  ));
}

type ReviewImpactViewProps = {
  projectCostCodes: ReviewProjectImpact_ProjectCostCodeFragment[];
  newBudgetRows: ReviewImpactNewBudgetRow[];
  currentItemCount: number;
  newItemCount: number;
  isReplacingConfig: boolean;
  scheduleImpactVariables: ReviewProjectScheduleImpactQueryVariables;
};

/** The step's layout, written once because both branches shape their data the same way. */
function ReviewImpactView(props: ReviewImpactViewProps) {
  const {
    projectCostCodes,
    newBudgetRows,
    currentItemCount,
    newItemCount,
    isReplacingConfig,
    scheduleImpactVariables,
  } = props;

  /**
   * Deliberately not in the step's `queriesResult`: projecting the schedule re-runs the simulator, so the budget
   * table would sit behind a 30s-timeout HTTP call for a number it doesn't need. The card and the table skeleton
   * themselves instead.
   */
  const scheduleImpactQuery = useReviewProjectScheduleImpactQuery({ variables: scheduleImpactVariables });

  const rows = useMemo(
    () => createProjectBudgetRows(projectCostCodes, newBudgetRows, isReplacingConfig),
    [projectCostCodes, newBudgetRows, isReplacingConfig],
  );

  // The table shows each cost code's change, so the cards are the only place that needs the resulting budget
  const totals = reviewImpactBudgetTotals(projectCostCodes, rows);

  return (
    <CenteredLayout size="lg">
      <div css={Css.df.fdc.py3.gap8.$}>
        <div css={Css.df.fdc.gap2.$}>
          <ContentHeader
            title="Review Project Impact"
            description="Review the resulting budget, scope, and schedule before applying this configuration."
          />
          <ProjectImpactCards
            totals={totals}
            currentItemCount={currentItemCount}
            newItemCount={newItemCount}
            scheduleImpact={scheduleImpactQuery.data?.projectScheduleImpact}
          />
        </div>

        <div css={Css.df.fdc.gap2.$}>
          <ContentHeader title="Project Budget" description="Review the resulting budget by cost code." />
          <ProjectBudgetTable budgetRows={rows} />
        </div>

        <div css={Css.df.fdc.gap2.$}>
          <ContentHeader
            title="Schedule Impact"
            description="How this configuration shifts the project's milestones."
          />
          {queryResult(scheduleImpactQuery, {
            loading: () => <LoadingTable columns={4} />,
            data: ({ projectScheduleImpact }) => <ScheduleImpactTable milestones={projectScheduleImpact.milestones} />,
          })}
        </div>
      </div>
    </CenteredLayout>
  );
}
